import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import CALENDAR_ICON from "../img/calendar_icon.svg";
import IconButton from "../shared/IconButton";
import { Input } from "./ui/input";
import {
  formatIsoDateForDisplay,
  normalizeBirthDateValue,
  parseIsoDate,
  toIsoDate,
} from "../Utilities/dateUtils";

const YEARS_PER_PAGE = 12;
const MAX_YEARS_BACK = 120;

const MarketingBirthdayDatePicker = ({ value, onChange }) => {
  const calendarWrapperRef = useRef(null);

  const locale = useSelector((state) => state.mode.language) || undefined;

  const dateValue = normalizeBirthDateValue(value);
  const displayDateValue = formatIsoDateForDisplay(dateValue);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = toIsoDate(today);
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const minYear = currentYear - MAX_YEARS_BACK;

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  // 'days' | 'months' | 'years' — which drill-down level the popup body shows.
  const [viewMode, setViewMode] = useState("days");
  // Always initialise to the current month/year. The picker opens on "today"
  // regardless of any stored value; the user drills back via the year button.
  const [visibleMonth, setVisibleMonth] = useState(
    () => new Date(currentYear, currentMonth, 1)
  );

  // First year of the 12-cell year grid. Pages are anchored so the most recent
  // page ends exactly on the current year (no future years ever shown).
  const getYearPageStart = (year) => {
    const page = Math.floor((currentYear - year) / YEARS_PER_PAGE);
    return currentYear - (YEARS_PER_PAGE - 1) - page * YEARS_PER_PAGE;
  };
  const [yearPageStart, setYearPageStart] = useState(() =>
    getYearPageStart(visibleMonth.getFullYear())
  );

  const weekdayLabelFormatter = new Intl.DateTimeFormat(locale, {
    weekday: "short",
  });
  const monthShortFormatter = new Intl.DateTimeFormat(locale, {
    month: "short",
  });
  const monthLongFormatter = new Intl.DateTimeFormat(locale, {
    month: "long",
  });

  const weekdayLabels = Array.from({ length: 7 }, (_, index) =>
    weekdayLabelFormatter.format(new Date(2024, 0, 7 + index))
  );

  const selectedDate = parseIsoDate(dateValue);
  const visibleYear = visibleMonth.getFullYear();
  const visibleMonthIndex = visibleMonth.getMonth();

  // --- day grid ---
  const firstWeekdayIndex = new Date(visibleYear, visibleMonthIndex, 1).getDay();
  const daysInMonth = new Date(visibleYear, visibleMonthIndex + 1, 0).getDate();

  const dayCells = [];
  for (let i = 0; i < firstWeekdayIndex; i += 1) {
    dayCells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    dayCells.push(new Date(visibleYear, visibleMonthIndex, day));
  }
  while (dayCells.length % 7 !== 0) {
    dayCells.push(null);
  }

  const currentMonthDate = new Date(currentYear, currentMonth, 1);
  const nextMonthDate = new Date(visibleYear, visibleMonthIndex + 1, 1);
  const hasNextMonth = nextMonthDate <= currentMonthDate;
  
  const prevMonthDate = new Date(visibleYear, visibleMonthIndex - 1, 1);
  const minMonthDate = new Date(minYear, 0, 1);
  const hasPrevMonth = prevMonthDate >= minMonthDate;

  // --- months grid ---
  const monthCells = Array.from({ length: 12 }, (_, month) => ({
    month,
    label: monthShortFormatter.format(new Date(2024, month, 1)),
    isDisabled: visibleYear === currentYear && month > currentMonth,
    isSelected:
      selectedDate &&
      selectedDate.getFullYear() === visibleYear &&
      selectedDate.getMonth() === month,
  }));

  // --- years grid ---
  const yearCells = Array.from({ length: YEARS_PER_PAGE }, (_, index) => {
    const year = yearPageStart + index;
    return {
      year,
      isDisabled: year > currentYear || year < minYear,
      isSelected: selectedDate && selectedDate.getFullYear() === year,
    };
  });
  const hasOlderYears = yearPageStart > minYear;
  const hasNewerYears = yearPageStart + YEARS_PER_PAGE - 1 < currentYear;

  const monthYearLabel = `${monthLongFormatter.format(visibleMonth)} ${visibleYear}`;
  const yearRangeLabel = `${yearPageStart} – ${yearPageStart + YEARS_PER_PAGE - 1}`;

  useEffect(() => {
    if (!isCalendarOpen) return undefined;

    const handleClickOutside = (event) => {
      if (!calendarWrapperRef.current?.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCalendarOpen]);


  const resetToCurrentMonth = () => {
    setVisibleMonth(new Date(currentYear, currentMonth, 1));
    setViewMode("days");
  };

  const openCalendar = () => {
    resetToCurrentMonth();
    setIsCalendarOpen(true);
  };

  const toggleCalendar = () => {
    setIsCalendarOpen((prev) => {
      if (!prev) resetToCurrentMonth();
      return !prev;
    });
  };

  const handleDaySelect = (dayDate) => {
    const normalizedDayDate = new Date(dayDate);
    normalizedDayDate.setHours(0, 0, 0, 0);

    if (normalizedDayDate > today) return;

    onChange?.(toIsoDate(normalizedDayDate));
    setIsCalendarOpen(false);
    setViewMode("days");
  };

  const openYearsView = () => {
    setYearPageStart(getYearPageStart(visibleYear));
    setViewMode("years");
  };

  const handleYearSelect = (year) => {
    // Avoid landing on a future month when picking the current year.
    const safeMonth =
      year === currentYear && visibleMonthIndex > currentMonth
        ? currentMonth
        : visibleMonthIndex;
    setVisibleMonth(new Date(year, safeMonth, 1));
    setViewMode("months");
  };

  const handleMonthSelect = (month) => {
    setVisibleMonth(new Date(visibleYear, month, 1));
    setViewMode("days");
  };

  // The header arrows mean different things per view: month, year, or year-page.
  const goPrev = () => {
    if (viewMode === "days") {
      if (!hasPrevMonth) return;
      setVisibleMonth(
        (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
      );
    } else if (viewMode === "months") {
      if (visibleYear <= minYear) return;
      setVisibleMonth(
        (prev) => new Date(prev.getFullYear() - 1, prev.getMonth(), 1)
      );
    } else {
      if (!hasOlderYears) return;
      setYearPageStart((prev) => prev - YEARS_PER_PAGE);
    }
  };

  const goNext = () => {
    if (viewMode === "days") {
      if (!hasNextMonth) return;
      setVisibleMonth(
        (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
      );
    } else if (viewMode === "months") {
      if (visibleYear >= currentYear) return;
      setVisibleMonth(
        (prev) => new Date(prev.getFullYear() + 1, prev.getMonth(), 1)
      );
    } else {
      if (!hasNewerYears) return;
      setYearPageStart((prev) => prev + YEARS_PER_PAGE);
    }
  };

  const prevDisabled =
    (viewMode === "days" && !hasPrevMonth) ||
    (viewMode === "months" && visibleYear <= minYear) ||
    (viewMode === "years" && !hasOlderYears);
  const nextDisabled =
    (viewMode === "days" && !hasNextMonth) ||
    (viewMode === "months" && visibleYear >= currentYear) ||
    (viewMode === "years" && !hasNewerYears);

  const headerLabel =
    viewMode === "days"
      ? monthYearLabel
      : viewMode === "months"
      ? String(visibleYear)
      : yearRangeLabel;

  const handleTitleClick = () => {
    if (viewMode === "years") {
      setViewMode("days");
    } else {
      openYearsView();
    }
  };

  const isSelectedDate = (dayDate) =>
    selectedDate && toIsoDate(dayDate) === toIsoDate(selectedDate);

  return (
    <div className="marketing-date-wrapper" ref={calendarWrapperRef}>
      <Input
        type="text"
        value={displayDateValue}
        placeholder="00 - 00 - 0000"
        dir="ltr"
        className="marketing-date-input"
        data-testid="marketing-birthday-input"
        readOnly
        onClick={openCalendar}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openCalendar();
          }
        }}
      />
      <div className="marketing-date-adornment">
        <IconButton
          className="marketing-icon-btn p-2"
          data-testid="marketing-birthday-calendar-toggle"
          onClick={toggleCalendar}
        >
          <img src={CALENDAR_ICON}></img>
        </IconButton>
      </div>
      {isCalendarOpen && (
        <div
          className="marketing-date-popup"
          role="dialog"
          aria-modal="false"
          data-testid="marketing-birthday-calendar"
        >
          <div className="marketing-date-popup-header">
            <IconButton
              className={`marketing-date-nav-btn ${prevDisabled ? "is-disabled" : ""}`}
              onClick={goPrev}
              data-testid="marketing-birthday-calendar-prev"
            >
              <ChevronLeft size={16} />
            </IconButton>
            <button
              type="button"
              className="marketing-date-title-btn"
              onClick={handleTitleClick}
              data-testid="marketing-birthday-calendar-title"
            >
              <span>{headerLabel}</span>
              <ChevronDown size={14} />
            </button>
            <IconButton
              className={`marketing-date-nav-btn ${nextDisabled ? "is-disabled" : ""}`}
              onClick={goNext}
              data-testid="marketing-birthday-calendar-next"
            >
              <ChevronRight size={16} />
            </IconButton>
          </div>

          {viewMode === "days" && (
            <>
              <div className="marketing-date-weekdays" aria-hidden="true">
                {weekdayLabels.map((label) => (
                  <span key={label} className="marketing-date-weekday">
                    {label}
                  </span>
                ))}
              </div>

              <div className="marketing-date-days">
                {dayCells.map((dayDate, index) => {
                  if (!dayDate) {
                    return (
                      <span
                        key={`empty-${index}`}
                        className="marketing-date-day-placeholder"
                      />
                    );
                  }

                  const isDisabled = toIsoDate(dayDate) > maxDate;
                  const isSelected = isSelectedDate(dayDate);

                  return (
                    <button
                      key={toIsoDate(dayDate)}
                      type="button"
                      className={`marketing-date-day ${isSelected ? "is-selected" : ""}`}
                      onClick={() => handleDaySelect(dayDate)}
                      disabled={isDisabled}
                      data-testid={`marketing-birthday-day-${toIsoDate(dayDate)}`}
                    >
                      {dayDate.getDate()}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {viewMode === "months" && (
            <div
              className="marketing-date-period-grid"
              data-testid="marketing-birthday-calendar-months"
            >
              {monthCells.map((cell) => (
                <button
                  key={cell.month}
                  type="button"
                  className={`marketing-date-period-cell ${cell.isSelected ? "is-selected" : ""}`}
                  onClick={() => handleMonthSelect(cell.month)}
                  disabled={cell.isDisabled}
                  data-testid={`marketing-birthday-month-${cell.month}`}
                >
                  {cell.label}
                </button>
              ))}
            </div>
          )}

          {viewMode === "years" && (
            <div
              className="marketing-date-period-grid"
              data-testid="marketing-birthday-calendar-years"
            >
              {yearCells.map((cell) => (
                <button
                  key={cell.year}
                  type="button"
                  className={`marketing-date-period-cell ${cell.isSelected ? "is-selected" : ""}`}
                  onClick={() => handleYearSelect(cell.year)}
                  disabled={cell.isDisabled}
                  data-testid={`marketing-birthday-year-${cell.year}`}
                >
                  {cell.year}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketingBirthdayDatePicker;
